/* eslint-disable react/no-unescaped-entities */
import React from "react";
import Image from "next/image";
import NewLayout from "@components/new-ui/Layout";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";
import CTAButtons from "@components/new-ui/CTAButtons";
import FeatureInfoCard from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import ClientLogos, { LogoObject } from "@components/new-ui/ClientLogos";
import CTASection from "@components/new-ui/CTASection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import AwardsSection from "@components/new-ui/AwardsSection";
import { container_classname, sub_section_heading } from "@components/new-ui/constants";
import YellowCTAhref from "@components/yellowCTAhref";

const clientLogosData: LogoObject[] = [
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/adeer.png", alt: "adeer", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/credai.png", alt: "credai", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/estative.png", alt: "estative", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/housiey.png", alt: "housiey", width: 160, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/inspira.png", alt: "inspira", width: 160, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/lansum.png", alt: "lansum", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/luxprop.png", alt: "luxprop", width: 140, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/m3m.png", alt: "m3m", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/nationwide.png", alt: "nationwide", width: 180, height: 60 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/propleaf.png", alt: "propleaf", width: 100, height: 40 },
    { url: "https://assets.gallabox.com/gb-home/new-landing/client-logos/real-estate/range.png", alt: "range", width: 160, height: 40 },
];

const ExtracurricularMultichannelMessaging: React.FC = () => {
    const statsData = [
        { percentage: "59%", label: "Reduction in manual intervention" },
        { percentage: "42%", label: "Increase in engagement rates" },
        { percentage: "39%", label: "Growth in business profits" },
    ];

    return (
        <NewLayout
            description="Convert launch campaigns into bookings faster. Automate Instagram inquiries, manage buyer communication on WhatsApp, track payments, and reduce sales team workload for residential projects."
            title="WhatsApp & Instagram Automation for Real Estate Developers | Gallabox"
            canonical="https://gallabox.com/industry/real-estate/residential-real-estate/multichannel-messaging"
            ogAttributes={{
                image: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-og.png",
            }}
            schema={{
                heroOrOgImageAlt: "extracurricular-education-multichannel-messaging",
                heroOrOgImageDescription:
                    "Move your extracurricular prospects from curiosity to commitment faster. Capture leads from Instagram, automate WhatsApp communication, and handle inquiries, bookings, and payments while you focus on nurturing talent.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-og.png",
            }}
        >
            {/* Custom Hero Section */}
            <section className={`${container_classname} flex md:flex-row flex-col md:py-20 md:pt-40 py-12 gap-10 gap-x-20 items-center`}>
                <div className="md:w-1/2 w-full">
                    <p className="text-base font-semibold mb-3 text-[#3364FA]" style={{ color: "#3364FA" }}>
                        Residential Real Estate
                    </p>

                    <h1 className="text-black md:text-7xl text-5xl mb-12 font-bold" style={{ fontFamily: "Degular Variable" }}>
                        Turn property launch buzz into booked units
                    </h1>

                    <p className="text-black text-xl mb-8">
                        Convert project interest into confirmed sales faster. Capture inquiries from Instagram campaigns, automate WhatsApp buyer communication, and handle site
                        visit scheduling, payment tracking, and updates while your team focuses on high-value closures.
                    </p>

                    <div className="flex gap-6">
                        <CTAButtons primaryButtonText="Try it now for free!" />
                    </div>
                </div>

                {/* Right side - Image with overlays */}
                <div className="md:w-1/2 w-full relative">
                    <div className="relative w-full h-full">
                        {/* Main image */}
                        <div className="relative w-full aspect-[540/520] rounded-lg overflow-hidden">
                            <Image
                                width={540}
                                height={520}
                                src="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-hero.png"
                                alt="Extracurricular Education"
                                className="w-full h-full object-cover rounded-lg"
                            />

                            {/* Instagram Icon Overlay - Square with gradient */}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <StatsInfo statsInfo={statsData} backgroundColor="bg-gray-50" />

            {/* Trusted by Section */}
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">Trusted by 10,000+ businesses</h3>
                <ClientLogos logos={clientLogosData} />
            </section>

            <section className={`${container_classname} py-20`}>
                <h2 className={`${sub_section_heading} text-center mb-12`} style={{ fontFamily: "Degular Display" }}>
                    Who is this for?
                </h2>
                <div className="grid md:grid-cols-5 grid-cols-1 gap-6">
                    {[
                        {
                            img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/firms.png",
                            title: "Residential builders",
                        },
                        {
                            img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/agents.png",
                            title: "Real estate developers",
                        },
                        {
                            img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/home.png",
                            title: "Project owners",
                        },
                        {
                            img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/resident.png",
                            title: "Apartment developers",
                        },
                        {
                            img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/brokers.png",
                            title: "Township developers",
                        },
                    ].map((item, idx) => (
                        <div key={idx} className={"bg-blue-50 rounded-lg p-8 flex flex-col items-start justify-center min-h-[140px]"}>
                            <div className="mb-6 flex justify-center">
                                <Image width={64} height={64} src={item.img} alt={item.title} className=" object-cover rounded-lg" />
                            </div>
                            <p className="font-semibold text-lg leading-tight text-black">{item.title}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* What's holding back section */}
            <section className="bg-blue-50 py-20">
                <div className={container_classname}>
                    <h2 className={`${sub_section_heading} text-left mb-12`} style={{ fontFamily: "Degular Display" }}>
                        What's preventing faster sell-through rates?
                    </h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 grid-cols-1 gap-6">
                        {[
                            {
                                title: "Launch campaigns generate tough inquiry floods",
                                description:
                                    // eslint-disable-next-line quotes
                                    "Your Instagram campaign for Phase 2 gets 400 comments in 48 hours. Your sales team of five is completely buried. By the time they reach out to everyone, half the prospects have already visited competing projects that responded faster.",
                            },
                            {
                                title: "Site visits don't happen as scheduled",
                                description:
                                    "You've planned for 15 family visits this Saturday. Seven don't show up. No call, no message, just absent. Your sales team prepared extensive presentations and arranged site logistics for people who never appeared.",
                            },
                            {
                                title: "Payment collection consumes lot of team time",
                                description:
                                    "Booking amounts are pending from 35 buyers. The second instalment is overdue for 28 customers. Your team tracks reminders in spreadsheets, sends manual WhatsApp messages, and makes calls that go to voicemail.",
                            },
                            {
                                title: "Inquiries come from everywhere with no central tracking",
                                description:
                                    "Two sales executives unknowingly promise the same premium 3BHK to different buyers because there’s no real-time inventory visibility. One family gets it. The other feels misled and walks away, telling friends about the experience.",
                            },
                            {
                                title: "Unit availability confusion creates sales conflicts",
                                description:
                                    // eslint-disable-next-line quotes
                                    "Your Instagram campaign for Phase 2 gets 400 comments in 48 hours. Your sales team of five is completely buried. By the time they reach out to everyone, half the prospects have already visited competing projects that responded faster.",
                            },
                            {
                                title: "Buyers constantly ask for construction updates",
                                description:
                                    "'When will the 8th-floor slab be cast?' 'Can I visit the site this weekend?' Your sales team fields 50+ update requests daily. Construction queries should be handled separately, but they're not.",
                            },
                            {
                                title: "Marketing spend vanishes into lead black holes",
                                description:
                                    "You spent $10,000 on Facebook and Instagram ads last quarter. Leads poured in somewhere. Your team called maybe half. A quarter were unqualified. You have zero visibility into which campaigns drove actual bookings.",
                            },
                            {
                                title: "Post-booking buyers become anxious and cancel",
                                description:
                                    "After paying the booking amount, buyers hear nothing meaningful for months. Construction timelines get vague. Anxiety builds about their investment. Some request refunds. Others explore exit clauses in contracts.",
                            },
                        ].map((item, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-6 shadow-sm">
                                <h3 className="font-semibold text-xl mb-3">{item.title}</h3>
                                <p className="text-gray-600">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Gallabox can fix section */}
            <section className={`${container_classname} pt-20`}>
                <h2 className={`${sub_section_heading} mb-8`} style={{ fontFamily: "Degular Display" }}>
                    Gallabox turns inquiry chaos into systematic sales operations
                </h2>
                <p className="text-xl text-gray-700 mb-4">
                    350 Instagram comments from a launch announcement. WhatsApp messages from six channel partners about unit availability. Buyers are asking when construction will
                    be completed. This is manageable with Gallabox:
                </p>
            </section>

            {/* Launch campaign responses section */}
            <FeatureInfoCard
                mainTitle="Launch campaign responses happen instantly and intelligently"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-1.png"
                featureInfoList={[
                    "Let’s say someone comments, 'Price for 2BHK?' on your project announcement post.",
                    "They immediately receive a DM: 'Great choice! Our 2BHK units start at ₹45L. Are you looking for lower floors or higher floors? When would you like to visit the site?' All powered by AI chatbots.",
                    "Their responses reveal intent level. Serious buyers with budgets get tagged ('2BHK - Budget Confirmed - Site Visit Requested') and routed to sales with qualification already complete. Browse-mode prospects get information without consuming sales team bandwidth.",
                ]}
            />

            {/* Common buyer questions section */}
            <FeatureInfoCard
                mainTitle="Common buyer questions get handled 24/7 automatically"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-2.png"
                featureInfoList={[
                    "'What's the price per square foot?' 'Is clubhouse access included?' 'What floor plans are available?' 'When is possession?'",
                    "Standard questions get accurate, instant responses any time. Complex queries like layout customization or payment restructuring route to your sales team with full conversation context—buyers never repeat themselves.",
                ]}
            />

            {/* Site visits scheduling section */}
            <FeatureInfoCard
                mainTitle="Site visits get scheduled smoothly with accountability"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-3.png"
                featureInfoList={[
                    "Prospects see available time slots, select their preference, and confirm—whether they came from Instagram, WhatsApp, or channel partners.",
                    "Reminders go out 48 hours before, 24 hours before, and 2 hours before via WhatsApp with location links and contact details. Buyers confirm attendance or reschedule with one tap. No-shows drop 60% because people commit more seriously when they've confirmed multiple times.",
                ]}
            />

            {/* Payment reminders section */}
            <FeatureInfoCard
                mainTitle="Payment reminders and collection run automatically"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-4.png"
                featureInfoList={[
                    "Set the payment flow once — booking amount in 7 days, milestone-based construction payments, and possession dues 30 days before handover.",
                    "Buyers receive WhatsApp reminders with payment links. Transactions complete instantly. Confirmations update your dashboard in real-time. Your accounts team monitors exceptions instead of chasing every payment.",
                ]}
            />

            {/* Unified inbox section */}
            <FeatureInfoCard
                mainTitle="Every inquiry channels into one intelligent system"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-5.png"
                featureInfoList={[
                    "Instagram DM, channel partner WhatsApp, website form, site walk-in—everything converges in a unified inbox with complete context.",
                    "Your sales manager sees which units are under discussion with whom, which channel partners deliver quality leads, which buyers are ready to close. Tag by budget range, unit type, timeline, or deal stage. Your team operates from shared truth, not scattered information.",
                ]}
            />

            {/* Construction progress section */}
            <FeatureInfoCard
                mainTitle="Construction progress reaches buyers without manual effort"
                imageUrl="https://assets.gallabox.com/gb-home/industries/multichannel-messaging/rre-mm-6.png"
                featureInfoList={[
                    "Send automated milestone updates like: foundation complete, 5th floor slab cast, plastering in progress, external facade work started.",
                    "Share photos and videos of actual progress. Buyers feel connected to their investment instead of anxious about delays. Cancellation requests decrease. They refer friends because they feel informed and valued.",
                ]}
            />

            {/* How Gallabox unlocks growth section */}
            <section className="py-20" style={{ background: "#E6EEFF" }}>
                <div className={container_classname}>
                    <h2 className={`${sub_section_heading} text-center mb-12`} style={{ fontFamily: "Degular Display" }}>
                        How Gallabox accelerates inventory clearance for developers
                    </h2>
                    <div className="grid md:grid-cols-2 grid-cols-1 gap-8">
                        {[
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/box-1.png",
                                title: "Convert launch campaigns into qualified pipelines immediately",
                                description:
                                    "AI responds to Instagram inquiry surges within seconds, qualifies budget and timeline, and routes hot prospects to sales before they explore alternatives. Your campaign ROI multiplies because fewer leads escape.",
                            },
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/box-2.png",
                                title: "Maximize site visit conversion productivity",
                                description:
                                    "When buyers show up, your team has their profile, preferences, and budget ready. Conversations start from 'Let me show you the exact unit type you're looking for' instead of basic qualification questions. Conversion rates climb.",
                            },
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/box-4.png",
                                title: "Accelerate cash flow through systematic collection",
                                description:
                                    "Payment reminders with embedded links reduce collection cycles from weeks to days. Buyers pay on schedule because the process requires minimal effort. Your project funding milestones get hit consistently.",
                            },
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/eye.png",
                                title: "Unify all lead sources for complete visibility",
                                description:
                                    "Direct inquiries, channel partners, Instagram campaigns, walk-ins—everything flows into one system. Dashboards show which sources drive bookings, which units move fastest, where to optimize marketing investment.",
                            },
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/dollar.svg",
                                title: "Scale sales without proportionally scaling headcount",
                                description:
                                    "Automation handles inquiry response, qualification, scheduling, payment reminders, and updates. Your sales team focuses exclusively on viewings, negotiations, and closures. Revenue per salesperson increases dramatically.",
                            },
                            {
                                img: "https://assets.gallabox.com/gb-home/industries/multichannel-messaging/box-3.png",
                                title: "Reduce cancellations through transparent engagement",
                                description:
                                    "Regular updates, instant query resolution, and systematic communication build confidence. Satisfied buyers complete payments on time, don't invoke cancellation clauses, and become referral sources for future phases.",
                            },
                        ].map((item, idx) => (
                            <div key={idx} className="bg-white rounded-lg p-6 shadow-sm flex gap-4">
                                <Image width={60} height={60} src={item.img} alt={item.title} className="w-12 h-12 object-cover rounded-lg" />
                                <div className=" flex flex-col items-start justify-center">
                                    <h3 className="font-semibold text-2xl mb-4 text-gray-900">{item.title}</h3>
                                    <p className="text-gray-600">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <YellowCTAhref
                cardStyles={{
                    cardColor: "linear-gradient(129deg, #E9D8FD 0.69%, #EDFDFD 98.6%)",
                    cardTextColor: "black",
                    buttonBackgroundColor: "bg-blue-color",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "30px",
                }}
                buttonText="Test now"
                buttonWithoutIcon
                buttonStyles={{ height: "100%", alignItems: "center", padding: "0px" }}
                link1="https://wame.pro/residential-real-estate"
                customText={<div className={"text-2xl md:text-3xl font-bold relative inline-block"}>Try our AI chatbot for residential real estate use cases! </div>}
            />

            {/* Integrations Section */}
            <IntegrationSection isLight={true} />

            {/* Recognition Section */}
            <AwardsSection />

            {/* Final CTA Section */}
            <CTASection title="Convert launches into bookings systematically, not chaotically" description="Take Gallabox for a 7-day ride" primaryButtonText="Book a demo" />
        </NewLayout>
    );
};

export default ExtracurricularMultichannelMessaging;
